import { forceLogout, forceAdminLogout } from '../useAuth'
import { downloadFromApiClient } from '../../utils/File'

import { getErrorMessageForCustomer } from './customer'
import { getErrorMessageForAdmin } from './admin'

import type { FetchError } from 'ofetch'

import { useAuthErrorStore } from '@/stores/authErrorStore'
import {
  useRuntimeConfig,
  useFetch,
  useNuxtApp,
} from '#imports'

type UseFetch<ResT, ErrorT> = typeof useFetch<ResT, ErrorT>
type ApiClientOptions<Res> = Parameters<typeof useFetch<Res>>[1]
export type UseFetchApi<Res, Args extends any[] = []> = (
  options: ApiClientOptions<Res>,
  ...args: Args
) => ReturnType<typeof useFetch<Res>>

/**
 * useFetch の型定義を参考にしている。
 * - ResT は本来 void だが、利用側で ResT を定義しないと method が get になるため any にしている。
 * - url は環境ごとの baseUrl 以降のパスを記載する。
 */
type UseApi = <ResT = any, ErrorT = FetchError>(
  url: Parameters<UseFetch<ResT, ErrorT>>[0],
  options?: Parameters<UseFetch<ResT, ErrorT>>[1]
) => ReturnType<UseFetch<ResT, ErrorT>>

/**
 * API クライアントのベース
 * @param url URL (ex: /rest/auth/user)
 * @param options オプション
 * @returns useFetch のレスポンス
 */
const useApiBase: UseApi = (url, options = {}) => {
  const config = useRuntimeConfig()
  const app = useNuxtApp()

  return useFetch(url, {
    ...options,
    baseURL: config.public.baseAPI,
    credentials: 'include',
    watch: options.watch ?? false,
    headers: {
      ...options.headers,
      'X-Language': app.$i18n.locale.value,
    },
    onRequestError(context) {
      // ネットワークエラーなどの検知.
      app.$sentryCaptureException(context.error)

      if (
        options.onRequestError
        && typeof options.onRequestError === 'function'
      ) {
        options.onRequestError(context)
      }
    },
    onResponseError(context) {
      if (context.error) {
        app.$sentryCaptureException(context.error)
      }

      if (
        options.onResponseError
        && typeof options.onResponseError === 'function'
      ) {
        options.onResponseError(context)
      }
    },
  })
}

/**
 * カスタマー用の API クライアント
 * @param url URL (ex: /rest/auth/user)
 * @param options オプション
 * @returns useFetch のレスポンス
 */
export const useApiForCustomer: UseApi = (url, options = {}) => {
  const app = useNuxtApp()
  return useApiBase(url, {
    ...options,
    headers: {
      ...options.headers,
    },
    onResponse(context) {
      // MEMO: ログアウト処理は onResponse ではなく onResponseError で行う方が良いかもしれない
      if (context.response.status === 401) {
        forceLogout()
      }
      else if (context.response._data?.errorCode === 'IP_NOT_ALLOWED') {
        const authErrorStore = useAuthErrorStore()
        authErrorStore.setErrorMessage(context.response._data?.message)

        forceLogout()
      }

      if (options.onResponse && typeof options.onResponse === 'function') {
        options.onResponse(context)
      }
    },
    onResponseError(context) {
      const errorCode = context.response._data?.errorCode
      if (errorCode) {
        const message = context.response._data?.message ?? ''
        const newMessage = getErrorMessageForCustomer(errorCode, message, app.$i18n.t)
        context.response._data.message = newMessage
      }

      if (
        options.onResponseError
        && typeof options.onResponseError === 'function'
      ) {
        options.onResponseError(context)
      }
    },
  })
}

/**
 * カスタマー用ファイルダウンロードの API クライアント
 * @param url URL (ex: /rest/auth/user)
 * @returns useFetch のレスポンス
 */
export const useApiForCustomerWithDl: UseApi = (url, options = {}) => {
  return useApiForCustomer(url, {
    ...options,
    onResponse(context) {
      if (context.response.ok) {
        // ファイルをダウンロードする
        downloadFromApiClient(
          context.response._data,
          context.response.headers.get('Content-Disposition'),
          context.response.headers.get('Content-Type'),
        )
      }
    },
  })
}

/**
 * Admin 用の API クライアント
 * @param url URL (ex: /rest/auth/user)
 * @param options オプション
 * @returns useFetch のレスポンス
 */
export const useApiForAdmin: UseApi = (url, options = {}) => {
  return useApiBase(url, {
    ...options,
    headers: {
      ...options.headers,
    },
    onResponse(context) {
      // MEMO: ログアウト処理は onResponse ではなく onResponseError で行う方が良いかもしれない
      if (context.response.status === 401) {
        forceAdminLogout()
      }

      if (options.onResponse && typeof options.onResponse === 'function') {
        options.onResponse(context)
      }
    },
    onResponseError(context) {
      const errorCode = context.response._data?.errorCode
      if (errorCode) {
        const message = context.response._data?.message ?? ''
        const newMessage = getErrorMessageForAdmin(errorCode, message)
        context.response._data.message = newMessage
      }

      if (
        options.onResponseError
        && typeof options.onResponseError === 'function'
      ) {
        options.onResponseError(context)
      }
    },
  })
}

/**
 * Admin 用ファイルダウンロードの API クライアント
 * @param url URL (ex: /rest/auth/user)
 * @returns useFetch のレスポンス
 */
export const useApiForAdminWithDl: UseApi = (url) => {
  return useApiForAdmin(url, {
    onResponse(context) {
      // ファイルをダウンロードする
      downloadFromApiClient(
        context.response._data,
        context.response.headers.get('Content-Disposition'),
        context.response.headers.get('Content-Type'),
      )
    },
  })
}

/**
 * パブリック用の API クライアント
 * @param url URL (ex: /rest/auth/user)
 * @param options オプション
 * @returns useFetch のレスポンス
 */
export const useApiForPublic: UseApi = (url, options = {}) => {
  const app = useNuxtApp()

  return useApiBase(url, {
    ...options,
    onResponseError(context) {
      const errorCode = context.response._data?.errorCode
      if (errorCode) {
        const message = context.response._data?.message ?? ''
        const newMessage = getErrorMessageForCustomer(errorCode, message, app.$i18n.t)
        context.response._data.message = newMessage
      }

      if (
        options.onResponseError
        && typeof options.onResponseError === 'function'
      ) {
        options.onResponseError(context)
      }
    },
  })
}

/**
 * パブリック用の API クライアントに API キーを付与
 * @param url URL (ex: /rest/auth/user)
 * @param options オプション
 * @returns useFetch のレスポンス
 */
export const useApiForPublicWithApiKey: UseApi = (url, options = {}) => {
  const app = useNuxtApp()
  const config = useRuntimeConfig()

  return useApiBase(url, {
    ...options,
    headers: {
      ...options.headers,
      'X-Api-Key': config.public.ECR_PUBLIC_API_KEY,
    },
    onResponseError(context) {
      const errorCode = context.response._data?.errorCode
      if (errorCode) {
        const message = context.response._data?.message ?? ''
        const newMessage = getErrorMessageForCustomer(errorCode, message, app.$i18n.t)
        context.response._data.message = newMessage
      }

      if (
        options.onResponseError
        && typeof options.onResponseError === 'function'
      ) {
        options.onResponseError(context)
      }
    },
  })
}

/**
 * ゲスト用の API クライアント
 * @param url URL (ex: /guest/common/upload)
 * @param options オプション
 * @returns useFetch のレスポンス
 */
export const useApiForGuest: UseApi = (url, options = {}) => {
  const app = useNuxtApp()

  return useApiBase(url, {
    ...options,
    onResponseError(context) {
      const errorCode = context.response._data?.errorCode
      if (errorCode) {
        const message = context.response._data?.message ?? ''
        const newMessage = getErrorMessageForCustomer(errorCode, message, app.$i18n.t)
        context.response._data.message = newMessage
      }

      if (
        options.onResponseError
        && typeof options.onResponseError === 'function'
      ) {
        options.onResponseError(context)
      }
    },
  })
}
